import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Icon } from '@iconify/react';
import {
  useAdminMovieDetail, useUpdateMovie, useUpdateEpisode, useAdminCategories,
} from '../../hooks/useMovie';
import { useAlert } from '../../components/ui/AlertModal';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { checkImageRatio, POSTER_SPEC, THUMBNAIL_SPEC } from '../../lib/image-ratio';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const RATING_OPTIONS = ['', 'G', 'PG', 'PG-13', 'R18', '12', '12A', '15', '18', 'NR'];
const CLASSIFICATION_AUTHORITY_OPTIONS = ['NFVCB', 'MPA', 'BBFC', 'Other'];
const CONTENT_DESCRIPTOR_OPTIONS = [
  'Violence', 'Strong Violence', 'Language', 'Sexual Content', 'Nudity',
  'Drugs', 'Alcohol', 'Horror', 'Frightening Scenes',
];

function EpisodeRow({ movieId, episode }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(episode.Title);
  const [description, setDescription] = useState(episode.Description || '');
  const [episodeNumber, setEpisodeNumber] = useState(episode.EpisodeNumber);
  const [seasonNumber, setSeasonNumber] = useState(episode.SeasonNumber ?? '');
  const [videoFile, setVideoFile] = useState(null);
  const updateEpisode = useUpdateEpisode();
  const { confirm } = useAlert();

  const handleSave = async () => {
    if (videoFile) {
      const ok = await confirm(
        'This permanently deletes the current video file (including its Bunny.net copy) and replaces it with the new upload. This can\'t be undone. Continue?',
        { title: 'Replace Episode Video', variant: 'warning', isConfirm: true, confirmLabel: 'Replace Video' }
      );
      if (!ok) return;
    }
    try {
      await updateEpisode.mutateAsync({
        episodeId: episode.Id,
        movieId,
        payload: {
          title: title.trim(),
          description,
          episodeNumber: episodeNumber || undefined,
          seasonNumber: seasonNumber === '' ? undefined : seasonNumber,
          videoFile,
        },
      });
      toast.success(videoFile ? 'Episode updated — old video deleted, new video is processing.' : 'Episode updated.');
      setEditing(false);
      setVideoFile(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update episode.'));
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-3 border-b border-white/[0.06] py-2.5 last:border-none">
        <span className="min-w-[68px] shrink-0 text-xs text-muted-foreground">
          {episode.SeasonNumber ? `S${episode.SeasonNumber}E${episode.EpisodeNumber}` : `Ep ${episode.EpisodeNumber}`}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{episode.Title}</span>
        <span className="shrink-0 text-[11px] text-muted-foreground/70">{episode.ApprovalStatus}</span>
        <Button type="button" size="sm" variant="secondary" onClick={() => setEditing(true)} className="shrink-0 gap-1.5">
          <Icon icon="ph:pencil-simple-bold" />
          Edit
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2.5 flex flex-col gap-3 rounded-lg border border-white/10 bg-black/20 p-3">
      <Field label="Title">
        <input className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Description">
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Episode #">
          <input
            type="number" className={inputClass} value={episodeNumber}
            onChange={(e) => setEpisodeNumber(e.target.value ? parseInt(e.target.value, 10) : '')}
          />
        </Field>
        <Field label="Season #" hint="(optional)">
          <input
            type="number" className={inputClass} value={seasonNumber}
            placeholder="(none)"
            onChange={(e) => setSeasonNumber(e.target.value ? parseInt(e.target.value, 10) : '')}
          />
        </Field>
      </div>
      <Field label="Replace video" hint="(leave blank to keep current)">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
          <Icon icon="ph:video-camera-bold" className="size-4 shrink-0" />
          <span className="truncate">{videoFile ? videoFile.name : 'Choose video file…'}</span>
          <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} className="hidden" />
        </label>
        {videoFile && (
          <p className="mt-1.5 text-xs text-[#f0a818]">
            The current video (and its Bunny.net copy) will be permanently deleted and replaced when you save.
          </p>
        )}
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={() => { setEditing(false); setVideoFile(null); }} className="gap-1.5">
          <Icon icon="ph:x-bold" />
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={handleSave} disabled={updateEpisode.isPending} className="gap-1.5">
          <Icon icon="ph:check-bold" />
          {updateEpisode.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function EditMovieModal({ movieId, onClose }) {
  const { data: movie, isLoading } = useAdminMovieDetail(movieId);
  const { data: categories = [] } = useAdminCategories();
  const updateMovie = useUpdateMovie();
  const { confirm } = useAlert();

  const [form, setForm] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [trailerFile, setTrailerFile] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);

  const [rating, setRating] = useState('');
  const [classificationAuthority, setClassificationAuthority] = useState('NFVCB');
  const [descriptors, setDescriptors] = useState([]);
  const [customDescriptors, setCustomDescriptors] = useState('');

  const toggleDescriptor = (d) => {
    setDescriptors((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  useEffect(() => {
    if (movie && !form) {
      setForm({
        title: movie.Title || '',
        description: movie.Description || '',
        director: movie.Director || '',
        cast: movie.Cast || '',
        releaseYear: movie.ReleaseYear || '',
        categoryIds: (movie.Categories || (movie.CategoryId ? [{ Id: movie.CategoryId }] : [])).map((c) => c.Id),
      });
      setRating(movie.Rating || '');
      setClassificationAuthority(movie.ClassificationAuthority || 'NFVCB');
      setDescriptors(movie.ContentDescriptors || []);
    }
  }, [movie, form]);

  const handleThumb = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setThumbnailFile(file);
    checkImageRatio(file, THUMBNAIL_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    setThumbPreview(URL.createObjectURL(file));
  };

  const handlePoster = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterFile(file);
    checkImageRatio(file, POSTER_SPEC).then((r) => {
      if (!r.ok) toast(r.message, { icon: '⚠️', duration: 7000 });
    });
    setPosterPreview(URL.createObjectURL(file));
  };

  const toggleCategory = (id) => setForm((f) => ({
    ...f,
    categoryIds: f.categoryIds.includes(id) ? f.categoryIds.filter((value) => value !== id) : [...f.categoryIds, id],
  }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required.'); return; }

    if (trailerFile) {
      const ok = await confirm(
        'This permanently deletes the current trailer file (including its Bunny.net copy) and replaces it with the new upload. This can\'t be undone. Continue?',
        { title: 'Replace Trailer', variant: 'warning', isConfirm: true, confirmLabel: 'Replace Trailer' }
      );
      if (!ok) return;
    }

    try {
      const allDescriptors = [...descriptors, ...customDescriptors.split(',').map((s) => s.trim()).filter(Boolean)];

      await updateMovie.mutateAsync({
        movieId,
        payload: {
          title: form.title.trim(),
          description: form.description,
          director: form.director,
          cast: form.cast,
          releaseYear: form.releaseYear || undefined,
          categoryIds: form.categoryIds,
          thumbnailFile,
          posterFile,
          trailerFile,
          rating,
          classificationAuthority,
          contentDescriptors: allDescriptors.join(','),
        },
      });
      toast.success(trailerFile ? 'Movie updated — old trailer deleted, new trailer is processing.' : 'Movie updated.');
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update movie.'));
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="ph:pencil-simple-bold" />
            Edit Movie
          </DialogTitle>
        </DialogHeader>

        {isLoading || !form ? (
          <DialogBody>
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          </DialogBody>
        ) : (
          <>
            <DialogBody className="gap-6">
              <Field label="Title">
                <input className={inputClass} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </Field>

              <Field label="Description">
                <textarea
                  className={`${inputClass} resize-none`}
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Director">
                  <input className={inputClass} value={form.director} onChange={(e) => setForm((f) => ({ ...f, director: e.target.value }))} />
                </Field>
                <Field label="Release Year">
                  <input
                    type="number" className={inputClass} value={form.releaseYear}
                    onChange={(e) => setForm((f) => ({ ...f, releaseYear: e.target.value ? parseInt(e.target.value, 10) : '' }))}
                  />
                </Field>
              </div>

              <Field label="Content Rating">
                <div className="grid grid-cols-2 gap-3">
                  <Select value={rating} onValueChange={setRating}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="— Select rating —" />
                    </SelectTrigger>
                    <SelectContent>
                      {RATING_OPTIONS.filter(Boolean).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={classificationAuthority} onValueChange={setClassificationAuthority}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CLASSIFICATION_AUTHORITY_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </Field>

              <Field label="Content Descriptors">
                <div className="flex flex-wrap gap-2">
                  {CONTENT_DESCRIPTOR_OPTIONS.map((d) => {
                    const active = descriptors.includes(d);
                    return (
                      <button
                        type="button"
                        key={d}
                        onClick={() => toggleDescriptor(d)}
                        className={cn(
                          'rounded-full border px-3.5 py-1.5 text-xs transition-colors',
                          active
                            ? 'border-primary/60 bg-primary/15 text-primary'
                            : 'border-white/15 bg-white/[0.02] text-muted-foreground hover:border-white/30'
                        )}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                <input
                  className={`${inputClass} mt-2`}
                  placeholder="Other descriptors (comma-separated) — not limited to the list above"
                  value={customDescriptors}
                  onChange={(e) => setCustomDescriptors(e.target.value)}
                />
              </Field>

              <Field label="Cast" hint="(comma-separated)">
                <input className={inputClass} value={form.cast} onChange={(e) => setForm((f) => ({ ...f, cast: e.target.value }))} />
              </Field>

              <Field label="Categories" hint="(select all that apply; first selection is primary)">
                <div className="flex flex-wrap gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                  {categories.map((c) => {
                    const selected = form.categoryIds.includes(c.Id);
                    return <label key={c.Id} className={cn('cursor-pointer rounded-full border px-3 py-1.5 text-xs transition-colors', selected ? 'border-primary/60 bg-primary/15 text-primary' : 'border-white/15 text-muted-foreground hover:border-white/30')}>
                      <input type="checkbox" className="sr-only" checked={selected} onChange={() => toggleCategory(c.Id)} />
                      {c.Name}
                    </label>;
                  })}
                  {categories.length === 0 && <span className="text-xs text-muted-foreground">No categories found.</span>}
                </div>
              </Field>

              <Field label="Poster" hint="(portrait 2:3 — shown in rows & grids; leave blank to keep current)">
                <div className="flex items-center gap-3">
                  {(posterPreview || movie.PosterUrl) && (
                    <img
                      src={posterPreview || movie.PosterUrl}
                      alt="Poster"
                      className="h-[90px] w-[60px] shrink-0 rounded-md object-cover"
                    />
                  )}
                  <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                    <Icon icon="ph:image-bold" className="size-4 shrink-0" />
                    <span className="truncate">{posterFile ? posterFile.name : 'Choose portrait poster…'}</span>
                    <input type="file" accept="image/*" onChange={handlePoster} className="hidden" />
                  </label>
                </div>
              </Field>

              <Field label="Thumbnail" hint="(landscape 16:9 — hero & title page; leave blank to keep current)">
                <div className="flex items-center gap-3">
                  {(thumbPreview || movie.ThumbnailUrl) && (
                    <img
                      src={thumbPreview || movie.ThumbnailUrl}
                      alt="Thumbnail"
                      className="h-[54px] w-[90px] shrink-0 rounded-md object-cover"
                    />
                  )}
                  <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                    <Icon icon="ph:image-bold" className="size-4 shrink-0" />
                    <span className="truncate">{thumbnailFile ? thumbnailFile.name : 'Choose image…'}</span>
                    <input type="file" accept="image/*" onChange={handleThumb} className="hidden" />
                  </label>
                </div>
              </Field>

              <Field label="Trailer" hint="(leave blank to keep current)">
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                  <Icon icon="ph:film-slate-bold" className="size-4 shrink-0" />
                  <span className="truncate">{trailerFile ? trailerFile.name : 'Choose video file…'}</span>
                  <input type="file" accept="video/*" onChange={(e) => setTrailerFile(e.target.files?.[0] || null)} className="hidden" />
                </label>
                {trailerFile && (
                  <p className="mt-1.5 text-xs text-[#f0a818]">
                    The current trailer (and its Bunny.net copy) will be permanently deleted and replaced when you save.
                  </p>
                )}
              </Field>

              {movie.Episodes?.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                    Episodes
                  </h4>
                  <div>
                    {movie.Episodes.map((ep) => <EpisodeRow key={ep.Id} movieId={movieId} episode={ep} />)}
                  </div>
                </div>
              )}
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose} className="gap-1.5">
                <Icon icon="ph:x-bold" />
                Close
              </Button>
              <Button type="button" onClick={handleSave} disabled={updateMovie.isPending} className="gap-1.5">
                <Icon icon="ph:upload-simple-bold" />
                {updateMovie.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
